// Centralized bilingual copy (English + Tamil) for the TNPSC Tamil marketing site.
export const translations = {
  en: {
    common: {
      brand: "TNPSC Tamil",
      appName: "Vetri Guide",
      presents: "TNPSC Tamil presents",
      tagline: "Knowledge · Effort · Victory",
      downloadPlayStore: "Get it on Google Play",
      downloadPlayStoreSub: "Download the app",
      freeTrial: "7-day free trial",
      bestValue: "Best value",
      save: "Save ₹50",
      perMonth: "/month",
      learnMore: "Learn more",
      getStarted: "Get started",
      subscribe: "Subscribe",
      tryFree: "Try free for 7 days",
      contactUs: "Contact us",
      backToHome: "Back to home",
    },
    nav: {
      home: "Home",
      features: "Features",
      pricing: "Pricing",
      about: "About",
      contact: "Contact",
      download: "Download app",
    },
    hero: {
      eyebrow: "Group 4 · Group 2/2A · TNUSRB · 2026",
      titleA: "Achieve your ambition with",
      titleB: "Vetri Guide.",
      titleC: "தன்னம்பிக்கையுடன்.",
      subtitle:
        "16,000+ MCQs, an AI Tutor that explains every wrong answer, weekly tests and weak-area analysis — built for Tamil Nadu aspirants who refuse to settle.",
      ctaPrimary: "Download on Play Store",
      ctaSecondary: "See features",
      trustLine: "Trusted by aspirants across all 38 districts of Tamil Nadu",
    },
    exams: {
      eyebrow: "Now covering",
      title: "One app. Every Tamil Nadu exam that matters.",
      subtitle:
        "From TNPSC Group 4 to Group 2/2A and Police recruitment — Vetri Guide now covers all the major exams Tamil Nadu aspirants care about.",
      items: [
        { name: "TNPSC Group 4", tag: "Available" },
        { name: "TNPSC Group 2 / 2A", tag: "New" },
        { name: "TNUSRB (Police)", tag: "New" },
      ],
    },
    stats: {
      mcq: "MCQs across the syllabus",
      subjects: "Core subjects covered",
      languages: "Languages — தமிழ் & English",
      rating: "Aspirant rating",
    },
    features: {
      eyebrow: "Why TNPSC Tamil",
      title: "Everything you need to clear Group 4, Group 2/2A & TNUSRB",
      subtitle:
        "Not just questions — a full preparation system designed around how Tamil Nadu aspirants actually study.",
      items: [
        {
          title: "16,000+ MCQs",
          desc: "Carefully curated questions across General Tamil, GS, Current Affairs, Polity, Geography, History, Economy and Aptitude.",
        },
        {
          title: "AI Tutor & Explanations",
          desc: "Stuck on a question? Tap and get a clear, step-by-step explanation — in Tamil or English — instantly.",
        },
        {
          title: "Daily, Weekly & Subject Tests",
          desc: "75-question daily, 100-question weekly, and focused subject practice — structured like the real exam.",
        },
        {
          title: "Rank Prediction",
          desc: "After each full-length test, see where you stand and how close you are to the cut-off.",
        },
        {
          title: "Weak Area Analysis",
          desc: "We track every wrong answer and point you to the exact chapter you need to revise.",
        },
        {
          title: "Monthly Current Affairs",
          desc: "Fresh 40-question current affairs updates on the 1st of every month — never miss what's important.",
        },
        {
          title: "Bilingual — தமிழ் + EN",
          desc: "Switch the entire app between Tamil and English with one tap. Study the way you think.",
        },
        {
          title: "Motivational Nudges",
          desc: "Daily reminders, streaks and quotes from those who made it — to keep you going on tough days.",
        },
      ],
    },
    subjects: {
      eyebrow: "Syllabus coverage",
      title: "All TNPSC subjects, in one app",
      subtitle:
        "Group 4, Group 2/2A & TNUSRB syllabus — broken down into focused, bite-sized practice.",
      list: [
        "General Tamil",
        "General Science",
        "Current Affairs",
        "Polity",
        "Geography",
        "History",
        "Economy",
        "Aptitude & Analytics",
      ],
    },
    how: {
      eyebrow: "How it works",
      title: "From download to government officer — in 4 steps",
      steps: [
        {
          n: "01",
          title: "Download free",
          desc: "Install TNPSC Tamil from Google Play in under 30 seconds.",
        },
        {
          n: "02",
          title: "Choose your exam",
          desc: "Group 4, Group 2/2A or TNUSRB — start your 7-day free trial.",
        },
        {
          n: "03",
          title: "Practice daily",
          desc: "Take daily tests, learn from AI explanations, track weak areas.",
        },
        {
          n: "04",
          title: "Clear the exam",
          desc: "Walk into the hall confident — and walk out with a government job.",
        },
      ],
    },
    pricing: {
      eyebrow: "Plans",
      title: "Simple, per-exam pricing",
      subtitle:
        "Pick the exam you're preparing for. Every plan is ₹250/month with a 7-day free trial. Cancel anytime.",
      plans: [
        {
          name: "Group 4",
          sub: "TNPSC Group 4",
          price: "₹250",
          cta: "Start 7-day trial",
          bullets: [
            "Full Group 4 syllabus coverage",
            "16,000+ MCQs + AI explanation",
            "AI Tutor & doubt clearance",
            "Daily / Weekly / Subject practice",
            "Rank prediction & weak-area analysis",
          ],
        },
        {
          name: "Group 2 / 2A",
          sub: "TNPSC Group 2 / 2A",
          price: "₹250",
          cta: "Start 7-day trial",
          highlighted: true,
          badge: "★ Popular",
          bullets: [
            "Full Group 2 / 2A syllabus coverage",
            "16,000+ MCQs + AI explanation",
            "AI Tutor & doubt clearance",
            "Daily / Weekly / Subject practice",
            "Rank prediction & weak-area analysis",
          ],
        },
        {
          name: "TNUSRB",
          sub: "TN Police (Constable / SI)",
          price: "₹250",
          cta: "Start 7-day trial",
          bullets: [
            "Full TNUSRB syllabus coverage",
            "16,000+ MCQs + AI explanation",
            "AI Tutor & doubt clearance",
            "Daily / Weekly / Subject practice",
            "Rank prediction & weak-area analysis",
          ],
        },
      ],
      assurances: ["Secure payments", "Cancel anytime", "7-day free trial"],
    },
    testimonials: {
      eyebrow: "Aspirant voices",
      title: "Real Tamil Nadu aspirants. Real results.",
      items: [
        {
          name: "Mathivanan, Madurai",
          role: "Group 4 aspirant",
          quote:
            "The AI Tutor explains in Tamil exactly the way my school teacher used to. I finally understand Polity.",
        },
        {
          name: "Narain, Coimbatore",
          role: "Group 2 aspirant",
          quote:
            "Daily tests built my discipline. Weak-area analysis told me exactly what to fix. Game changer.",
        },
        {
          name: "Priyadharshini, Salem",
          role: "Group 4 selected",
          quote:
            "I studied for 4 months on Vetri Guide. Cracked Group 4 in my first attempt. Thank you team!",
        },
        {
          name: "Karthik, Trichy",
          role: "Group 4 aspirant",
          quote:
            "Monthly current affairs is gold. 40 fresh questions every 1st — never miss anything important.",
        },
        {
          name: "Anitha, Tirunelveli",
          role: "TNUSRB aspirant",
          quote:
            "The bilingual switch is brilliant. Tamil for concepts, English for terms. Best of both worlds.",
        },
        {
          name: "Vignesh, Chennai",
          role: "Group 4 selected",
          quote:
            "Rank prediction kept me honest. Every full-length test felt like the real thing.",
        },
      ],
    },
    faq: {
      eyebrow: "Common questions",
      title: "Everything you wanted to ask",
      items: [
        {
          q: "Is the app really free to try?",
          a: "Yes. You get a 7-day free trial on any Vetri Guide plan you choose — no card required. After 7 days, choose to subscribe or stop using the app.",
        },
        {
          q: "Is content available in Tamil and English?",
          a: "Yes. The entire app — questions, options, explanations, AI Tutor — is fully bilingual. Toggle between தமிழ் and English anytime.",
        },
        {
          q: "How often is current affairs updated?",
          a: "Every month, on the 1st, we publish 40 fresh current affairs MCQs covering the previous month's important events.",
        },
        {
          q: "Can I cancel my subscription?",
          a: "Yes. You can cancel anytime from inside the app. You will continue to have access until the end of the billing period.",
        },
        {
          q: "Does this app work on iPhone?",
          a: "Currently, Vetri Guide is available on Android via Google Play Store. iOS support is on our roadmap.",
        },
        {
          q: "Is the AI Tutor really accurate?",
          a: "Our AI Tutor is tuned specifically for the TNPSC + TNUSRB syllabus and verified by Tamil Nadu educators. If you ever spot an issue, report it from the app and we'll fix it.",
        },
      ],
    },
    finalCta: {
      eyebrow: "Your future is one tap away",
      title: "Download Vetri Guide and start your 7-day free trial",
      subtitle:
        "Join thousands of Tamil Nadu aspirants who are practicing smarter — and clearing exams faster.",
      cta: "Download on Play Store",
    },
    referral: {
      eyebrow: "Referral program",
      title: "Invite 10 friends. Get 1 month free.",
      subtitle:
        "For every 10 friends who subscribe with your referral code, get 30 days free on all your subscriptions. Refer 10 more — get another month. No limits.",
      stat1: "10",
      stat1Label: "friends who subscribe",
      stat2: "1",
      stat2Label: "month free for you",
      stat3: "∞",
      stat3Label: "times you can repeat",
      bullet1: "Your unique referral code lives inside the app",
      bullet2: "Earned months stack across Group 4, Group 2/2A and TNUSRB",
      bullet3: "Works for all Vetri Guide Pro subscribers",
      cta: "Get your referral code in the app",
    },
    footer: {
      tagline:
        "TNPSC Tamil presents Vetri Guide — Tamil Nadu's most loved bilingual TNPSC prep app. Built with love, for the dreamers of Tamil Nadu.",
      productTitle: "Product",
      product: ["Features", "Pricing", "Download app"],
      companyTitle: "Company",
      company: ["About", "Contact"],
      legalTitle: "Legal",
      legal: ["Terms & Conditions", "Privacy Policy", "Refund Policy"],
      contactTitle: "Get in touch",
      rights: "All rights reserved.",
      madeIn: "Made with ♥ in Tamil Nadu",
    },
    contact: {
      eyebrow: "Contact",
      title: "We'd love to hear from you",
      subtitle:
        "Have a question, suggestion, or partnership idea? Reach out — we read every message.",
      emailLabel: "Email",
      phoneLabel: "Phone",
      hoursLabel: "Support hours",
      hours: "Mon – Sat, 9:00 AM – 7:00 PM IST",
      formName: "Your name",
      formEmail: "Your email",
      formMessage: "Your message",
      formSubmit: "Send message",
      formSuccess: "Thanks — we'll get back to you within 24 hours.",
    },
    about: {
      eyebrow: "About us",
      title: "Built by Tamilians, for Tamilians",
      lead: "TNPSC Tamil was born from a simple frustration — most exam prep apps in India treat Tamil aspirants as an afterthought. We're changing that.",
      p1: "We are a small team of educators, engineers and TNPSC veterans based in Tamil Nadu. We built TNPSC Tamil because we believe every aspirant — from Chennai to Cuddalore, from Coimbatore to Kanyakumari — deserves world-class prep in their own language.",
      p2: "Our mission is to help 1,00,000 Tamil Nadu aspirants land a government job by 2030. We obsess over question quality, explanation clarity, and respecting your time. No fluff. No filler. Just focused practice that gets you to the cut-off.",
      p3: "If you have ever felt overwhelmed by the TNPSC syllabus, scared of the cut-off, or unsure if you can actually do it — we built this app for you. You can. We'll help.",
      valuesTitle: "What we stand for",
      values: [
        {
          title: "Tamil-first",
          desc: "Every feature is designed in Tamil first, English second. Not the other way around.",
        },
        {
          title: "Honest pricing",
          desc: "₹250 a month. Cancel anytime. No tricks, no auto-renewals you didn't see coming.",
        },
        {
          title: "Aspirant obsessed",
          desc: "We read every message. Every feature comes from a real aspirant's request.",
        },
      ],
    },
    legal: {
      lastUpdated: "Last updated: January 2026",
    },
  },
  ta: {
    common: {
      brand: "TNPSC தமிழ்",
      appName: "வெற்றி கைடு",
      presents: "TNPSC தமிழ் அளிக்கும்",
      tagline: "அறிவு · முயற்சி · வெற்றி",
      downloadPlayStore: "Google Play-இல் பெறுங்கள்",
      downloadPlayStoreSub: "செயலியை பதிவிறக்கம் செய்யுங்கள்",
      freeTrial: "7-நாள் இலவச முயற்சி",
      bestValue: "சிறந்த சலுகை",
      save: "₹50 சேமிப்பு",
      perMonth: "/மாதம்",
      learnMore: "மேலும் அறிக",
      getStarted: "தொடங்குக",
      subscribe: "சந்தா எடு",
      tryFree: "7 நாள் இலவசம்",
      contactUs: "தொடர்பு கொள்ள",
      backToHome: "முகப்புக்கு திரும்ப",
    },
    nav: {
      home: "முகப்பு",
      features: "அம்சங்கள்",
      pricing: "திட்டங்கள்",
      about: "எங்களைப் பற்றி",
      contact: "தொடர்பு",
      download: "செயலியை பதிவிறக்கம்",
    },
    hero: {
      eyebrow: "குரூப் 4 · குரூப் 2/2A · TNUSRB · 2026",
      titleA: "உங்கள் இலக்கை அடையுங்கள்",
      titleB: "Vetri Guide-உடன்.",
      titleC: "தன்னம்பிக்கையுடன்.",
      subtitle:
        "16,000+ வினாக்கள், ஒவ்வொரு தவறான பதிலையும் விளக்கும் AI ஆசிரியர், வாராந்திர தேர்வுகள் மற்றும் பலவீன பகுதி பகுப்பாய்வு — தயக்கம் இல்லாத தமிழக மாணவர்களுக்காக.",
      ctaPrimary: "Play Store-இல் பதிவிறக்கம்",
      ctaSecondary: "அம்சங்களை பாருங்கள்",
      trustLine: "தமிழ்நாட்டின் 38 மாவட்டங்களிலும் மாணவர்களின் நம்பிக்கை",
    },
    exams: {
      eyebrow: "இப்போது கிடைக்கும்",
      title: "ஒரே செயலி. தமிழக முக்கிய தேர்வுகள் அனைத்திற்கும்.",
      subtitle:
        "TNPSC Group 4 முதல் Group 2/2A மற்றும் காவல்துறை தேர்வு வரை — Vetri Guide இப்போது தமிழக மாணவர்களுக்கு தேவையான அனைத்து முக்கிய தேர்வுகளையும் உள்ளடக்கியுள்ளது.",
      items: [
        { name: "TNPSC குரூப் 4", tag: "கிடைக்கிறது" },
        { name: "TNPSC குரூப் 2 / 2A", tag: "புதியது" },
        { name: "TNUSRB (காவல்)", tag: "புதியது" },
      ],
    },
    stats: {
      mcq: "பாடத்திட்ட வினாக்கள்",
      subjects: "முக்கிய பாடங்கள்",
      languages: "மொழிகள் — தமிழ் & English",
      rating: "மாணவர் மதிப்பீடு",
    },
    features: {
      eyebrow: "ஏன் TNPSC தமிழ்",
      title: "Group 4, Group 2/2A & TNUSRB வெற்றிக்கு தேவையான அனைத்தும்",
      subtitle:
        "வெறும் வினாக்கள் அல்ல — தமிழக மாணவர்கள் உண்மையில் படிக்கும் முறையில் வடிவமைக்கப்பட்ட முழுமையான தயாரிப்பு அமைப்பு.",
      items: [
        {
          title: "16,000+ வினாக்கள்",
          desc: "பொது தமிழ், பொது அறிவியல், நடப்பு நிகழ்வுகள், அரசியல், புவியியல், வரலாறு, பொருளாதாரம் மற்றும் திறன் – அனைத்து பாடங்களிலும்.",
        },
        {
          title: "AI ஆசிரியர் & விளக்கம்",
          desc: "சந்தேகம்? ஒரு டேப்-இல் தமிழில் அல்லது ஆங்கிலத்தில் தெளிவான விளக்கம் — உடனடியாக.",
        },
        {
          title: "தினசரி, வாராந்திர, பாட தேர்வுகள்",
          desc: "75-வினா தினசரி, 100-வினா வாராந்திர மற்றும் கவனமான பாட பயிற்சி — உண்மையான தேர்வைப் போல.",
        },
        {
          title: "தர முன்னறிவிப்பு",
          desc: "ஒவ்வொரு முழு தேர்வுக்கு பிறகும் நீங்கள் எங்கே இருக்கிறீர்கள், கட்-ஆஃப் எவ்வளவு அருகில் என்பதைக் காண்க.",
        },
        {
          title: "பலவீன பகுதி பகுப்பாய்வு",
          desc: "ஒவ்வொரு தவறான பதிலையும் கண்காணித்து, மீண்டும் படிக்க வேண்டிய சரியான அத்தியாயத்தை சுட்டிக்காட்டுகிறோம்.",
        },
        {
          title: "மாதாந்திர நடப்பு நிகழ்வுகள்",
          desc: "ஒவ்வொரு மாதமும் 1-ம் தேதி புதிய 40-வினா நடப்பு நிகழ்வுகள் — முக்கியமானதை எதையும் விடாதீர்கள்.",
        },
        {
          title: "இருமொழி — தமிழ் + EN",
          desc: "ஒரே டேப்பில் முழு செயலியையும் தமிழ் / ஆங்கிலம் மாற்றலாம். நீங்கள் சிந்திக்கும் முறையில் படியுங்கள்.",
        },
        {
          title: "ஊக்கமூட்டும் தினம்",
          desc: "தினசரி நினைவூட்டல்கள், ஸ்ட்ரீக்குகள், வென்றவர்களின் வார்த்தைகள் — கடினமான நாட்களிலும் தொடர.",
        },
      ],
    },
    subjects: {
      eyebrow: "பாடத்திட்ட பாதுகாப்பு",
      title: "அனைத்து TNPSC பாடங்களும், ஒரே செயலியில்",
      subtitle:
        "Group 4, Group 2/2A மற்றும் TNUSRB பாடத்திட்டம் — கவனமான, சிறிய பகுதிகளாக பயிற்சி.",
      list: [
        "பொது தமிழ்",
        "பொது அறிவியல்",
        "நடப்பு நிகழ்வுகள்",
        "அரசியல்",
        "புவியியல்",
        "வரலாறு",
        "பொருளாதாரம்",
        "திறன் & பகுப்பாய்வு",
      ],
    },
    how: {
      eyebrow: "எப்படி இயங்கும்",
      title: "பதிவிறக்கம் முதல் அரசு அதிகாரி வரை — 4 படிகளில்",
      steps: [
        {
          n: "01",
          title: "இலவசமாக பதிவிறக்கம்",
          desc: "30 நொடிகளில் Google Play-இல் இருந்து TNPSC தமிழ் பதிவிறக்குங்கள்.",
        },
        {
          n: "02",
          title: "தேர்வைத் தேர்வு செய்க",
          desc: "Group 4, Group 2/2A அல்லது TNUSRB — 7-நாள் இலவச முயற்சியில் தொடங்குங்கள்.",
        },
        {
          n: "03",
          title: "தினசரி பயிற்சி",
          desc: "தினசரி தேர்வு, AI விளக்கம், பலவீன பகுதி கண்காணிப்பு.",
        },
        {
          n: "04",
          title: "தேர்வில் வெற்றி",
          desc: "தன்னம்பிக்கையுடன் தேர்வு அறைக்குள் — அரசு வேலையுடன் வெளியே.",
        },
      ],
    },
    pricing: {
      eyebrow: "திட்டங்கள்",
      title: "எளிய, ஒவ்வொரு தேர்வுக்கும் விலை",
      subtitle:
        "நீங்கள் தயாராகும் தேர்வைத் தேர்ந்தெடுக்கவும். ஒவ்வொரு திட்டமும் ₹250/மாதம், 7-நாள் இலவச முயற்சியுடன். எப்போது வேண்டுமானாலும் ரத்து செய்யலாம்.",
      plans: [
        {
          name: "குரூப் 4",
          sub: "TNPSC Group 4",
          price: "₹250",
          cta: "7-நாள் இலவசம்",
          bullets: [
            "முழு Group 4 பாடத்திட்டம்",
            "16,000+ MCQ + AI விளக்கம்",
            "AI ஆசிரியர் & சந்தேக நிவாரணம்",
            "தினசரி / வாராந்திர / பாட பயிற்சி",
            "தர முன்னறிவிப்பு & பலவீன பகுதி பகுப்பாய்வு",
          ],
        },
        {
          name: "குரூப் 2 / 2A",
          sub: "TNPSC Group 2 / 2A",
          price: "₹250",
          cta: "7-நாள் இலவசம்",
          highlighted: true,
          badge: "★ பிரபலமானது",
          bullets: [
            "முழு Group 2 / 2A பாடத்திட்டம்",
            "16,000+ MCQ + AI விளக்கம்",
            "AI ஆசிரியர் & சந்தேக நிவாரணம்",
            "தினசரி / வாராந்திர / பாட பயிற்சி",
            "தர முன்னறிவிப்பு & பலவீன பகுதி பகுப்பாய்வு",
          ],
        },
        {
          name: "TNUSRB",
          sub: "தமிழக காவல்துறை (கான்ஸ்டபிள் / SI)",
          price: "₹250",
          cta: "7-நாள் இலவசம்",
          bullets: [
            "முழு TNUSRB பாடத்திட்டம்",
            "16,000+ MCQ + AI விளக்கம்",
            "AI ஆசிரியர் & சந்தேக நிவாரணம்",
            "தினசரி / வாராந்திர / பாட பயிற்சி",
            "தர முன்னறிவிப்பு & பலவீன பகுதி பகுப்பாய்வு",
          ],
        },
      ],
      assurances: ["பாதுகாப்பான கட்டணம்", "எப்போது வேண்டுமானாலும் ரத்து", "7-நாள் இலவசம்"],
    },
    testimonials: {
      eyebrow: "மாணவர் குரல்கள்",
      title: "உண்மையான தமிழக மாணவர்கள். உண்மையான முடிவுகள்.",
      items: [
        {
          name: "மதிவாணன், மதுரை",
          role: "Group 4 மாணவர்",
          quote:
            "AI ஆசிரியர் என் பள்ளி ஆசிரியர் சொல்லியது போலவே தமிழில் விளக்குகிறார். அரசியல் பாடம் இப்போது புரிகிறது.",
        },
        {
          name: "நரேன், கோயம்புத்தூர்",
          role: "Group 2 மாணவர்",
          quote:
            "தினசரி தேர்வுகள் என் ஒழுக்கத்தை வளர்த்தன. பலவீன பகுதி பகுப்பாய்வு என்ன சரிசெய்ய வேண்டும் என சொன்னது. மாற்றம் கொண்டு வந்தது!",
        },
        {
          name: "பிரியதர்ஷினி, சேலம்",
          role: "Group 4 தேர்வு பெற்றவர்",
          quote:
            "4 மாதங்கள் Vetri Guide-இல் படித்தேன். முதல் முயற்சியிலேயே Group 4 தேர்வு பெற்றேன். நன்றி குழு!",
        },
        {
          name: "கார்த்திக், திருச்சி",
          role: "Group 4 மாணவர்",
          quote:
            "மாதாந்திர நடப்பு நிகழ்வுகள் தங்கம். ஒவ்வொரு 1-ம் தேதியும் 40 புதிய வினாக்கள் — எதையும் விட மாட்டோம்.",
        },
        {
          name: "அனிதா, திருநெல்வேலி",
          role: "TNUSRB மாணவர்",
          quote:
            "மொழி மாற்றம் அற்புதம். கருத்துக்கு தமிழ், சொற்களுக்கு ஆங்கிலம் — இரண்டின் சிறந்த.",
        },
        {
          name: "விக்னேஷ், சென்னை",
          role: "Group 4 தேர்வு பெற்றவர்",
          quote:
            "தர முன்னறிவிப்பு என்னை நேர்மையாக வைத்திருந்தது. ஒவ்வொரு முழு தேர்வும் உண்மையான தேர்வு போல உணர்ந்தேன்.",
        },
      ],
    },
    faq: {
      eyebrow: "பொதுவான கேள்விகள்",
      title: "நீங்கள் கேட்க விரும்பிய அனைத்தும்",
      items: [
        {
          q: "செயலி உண்மையில் இலவசமாக முயற்சி செய்ய முடியுமா?",
          a: "ஆம். நீங்கள் தேர்ந்தெடுக்கும் எந்த Vetri Guide திட்டத்திற்கும் 7-நாள் இலவச முயற்சி கிடைக்கும் — அட்டை தேவையில்லை. 7 நாட்களுக்கு பின் சந்தா எடுக்கலாம் அல்லது நிறுத்தலாம்.",
        },
        {
          q: "உள்ளடக்கம் தமிழ் மற்றும் ஆங்கிலத்தில் கிடைக்குமா?",
          a: "ஆம். முழு செயலியும் — வினாக்கள், விருப்பங்கள், விளக்கங்கள், AI ஆசிரியர் — முழுமையாக இருமொழியில் உள்ளது. தமிழ் மற்றும் English-இடையே எப்போது வேண்டுமானாலும் மாறலாம்.",
        },
        {
          q: "நடப்பு நிகழ்வுகள் எவ்வளவு அடிக்கடி புதுப்பிக்கப்படும்?",
          a: "ஒவ்வொரு மாதமும் 1-ம் தேதி, முந்தைய மாதத்தின் முக்கிய நிகழ்வுகளை உள்ளடக்கிய 40 புதிய நடப்பு நிகழ்வு MCQ-களை வெளியிடுகிறோம்.",
        },
        {
          q: "என் சந்தாவை ரத்து செய்ய முடியுமா?",
          a: "ஆம். செயலிக்குள் இருந்து எப்போது வேண்டுமானாலும் ரத்து செய்யலாம். கட்டண காலத்தின் முடிவு வரை அணுகல் தொடரும்.",
        },
        {
          q: "இந்த செயலி iPhone-இல் வேலை செய்யுமா?",
          a: "தற்போது, Vetri Guide Google Play Store வழியாக Android-இல் கிடைக்கும். iOS ஆதரவு எங்கள் வரைபடத்தில் உள்ளது.",
        },
        {
          q: "AI ஆசிரியர் உண்மையில் துல்லியமாகவா?",
          a: "எங்கள் AI ஆசிரியர் TNPSC பாடத்திட்டத்திற்காக சிறப்பாக கட்டமைக்கப்பட்டு, தமிழக கல்வியாளர்களால் சரிபார்க்கப்பட்டுள்ளது. பிரச்சினை இருந்தால், செயலியிலிருந்து தெரிவியுங்கள், நாங்கள் சரிசெய்வோம்.",
        },
      ],
    },
    finalCta: {
      eyebrow: "உங்கள் எதிர்காலம் ஒரு டேப் தூரத்தில்",
      title: "Vetri Guide-ஐ பதிவிறக்கம் செய்து 7-நாள் இலவச முயற்சியை தொடங்குங்கள்",
      subtitle:
        "புத்திசாலித்தனமாக பயிற்சி செய்து தேர்வுகளில் வேகமாக வெற்றி பெறும் ஆயிரக்கணக்கான தமிழக மாணவர்களுடன் இணைக.",
      cta: "Play Store-இல் பதிவிறக்கம்",
    },
    referral: {
      eyebrow: "ரெஃபரல் திட்டம்",
      title: "10 நண்பர்களை அழையுங்கள். 1 மாதம் இலவசம்.",
      subtitle:
        "உங்கள் ரெஃபரல் குறியீடு வழியாக 10 நண்பர்கள் சந்தா எடுத்தால், உங்கள் அனைத்து சந்தாக்களுக்கும் 30 நாள் இலவசம். மேலும் 10 பேர் சேர்ந்தால் — மீண்டும் ஒரு மாதம். வரம்பற்றது!",
      stat1: "10",
      stat1Label: "சந்தா எடுக்கும் நண்பர்கள்",
      stat2: "1",
      stat2Label: "மாதம் உங்களுக்கு இலவசம்",
      stat3: "∞",
      stat3Label: "எத்தனை முறை வேண்டுமானாலும்",
      bullet1: "உங்கள் தனிப்பட்ட ரெஃபரல் குறியீடு செயலியில் கிடைக்கும்",
      bullet2: "சம்பாதித்த மாதங்கள் Group 4, Group 2/2A மற்றும் TNUSRB அனைத்திற்கும் சேரும்",
      bullet3: "அனைத்து Vetri Guide Pro சந்தாதாரர்களுக்கும் பொருந்தும்",
      cta: "செயலியில் ரெஃபரல் குறியீடு பெறுங்கள்",
    },
    footer: {
      tagline:
        "TNPSC தமிழ் அளிக்கும் Vetri Guide — தமிழ்நாட்டின் மிக நேசிக்கப்பட்ட இருமொழி TNPSC தயாரிப்பு செயலி. தமிழக கனவு காண்பவர்களுக்காக அன்புடன் கட்டப்பட்டது.",
      productTitle: "தயாரிப்பு",
      product: ["அம்சங்கள்", "திட்டங்கள்", "செயலியை பதிவிறக்கம்"],
      companyTitle: "நிறுவனம்",
      company: ["எங்களைப் பற்றி", "தொடர்பு"],
      legalTitle: "சட்டம்",
      legal: ["விதிமுறைகள் & நிபந்தனைகள்", "தனியுரிமை கொள்கை", "திரும்பப் பெறும் கொள்கை"],
      contactTitle: "தொடர்பு கொள்ள",
      rights: "அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.",
      madeIn: "தமிழ்நாட்டில் ♥ உடன் உருவாக்கப்பட்டது",
    },
    contact: {
      eyebrow: "தொடர்பு",
      title: "உங்களிடமிருந்து கேட்க விரும்புகிறோம்",
      subtitle:
        "கேள்வி, பரிந்துரை அல்லது கூட்டு யோசனை உள்ளதா? தொடர்பு கொள்ளுங்கள் — ஒவ்வொரு செய்தியையும் படிக்கிறோம்.",
      emailLabel: "மின்னஞ்சல்",
      phoneLabel: "தொலைபேசி",
      hoursLabel: "ஆதரவு நேரம்",
      hours: "திங்கள் – சனி, காலை 9:00 – மாலை 7:00 IST",
      formName: "உங்கள் பெயர்",
      formEmail: "உங்கள் மின்னஞ்சல்",
      formMessage: "உங்கள் செய்தி",
      formSubmit: "செய்தி அனுப்பு",
      formSuccess: "நன்றி — 24 மணி நேரத்திற்குள் பதிலளிப்போம்.",
    },
    about: {
      eyebrow: "எங்களைப் பற்றி",
      title: "தமிழர்களால், தமிழர்களுக்காக",
      lead: "TNPSC தமிழ் ஒரு எளிய ஏமாற்றத்திலிருந்து பிறந்தது — இந்தியாவில் பெரும்பாலான தேர்வுத் தயாரிப்பு செயலிகள் தமிழ் மாணவர்களை இரண்டாம் இடத்தில் வைக்கின்றன. அதை மாற்றுகிறோம்.",
      p1: "நாங்கள் தமிழ்நாட்டில் உள்ள கல்வியாளர்கள், பொறியாளர்கள் மற்றும் TNPSC அனுபவிகளின் சிறிய குழு. சென்னை முதல் கடலூர் வரை, கோயம்புத்தூர் முதல் கன்னியாகுமரி வரை — ஒவ்வொரு மாணவருக்கும் தாய்மொழியில் உலகத்தரம் வாய்ந்த தயாரிப்பு கிடைக்க வேண்டும் என நம்புகிறோம்.",
      p2: "2030-க்குள் 1,00,000 தமிழக மாணவர்களுக்கு அரசு வேலை வாங்கி தர உதவுவது எங்கள் நோக்கம். வினா தரம், விளக்கத் தெளிவு, உங்கள் நேரத்திற்கான மரியாதை — இவற்றிலேயே வாழ்கிறோம். உப்பு இல்லை. தேவையற்ற நிரப்பல் இல்லை. கட்-ஆஃப்-க்கு உங்களை எடுத்துச் செல்லும் கவனமான பயிற்சி மட்டுமே.",
      p3: "TNPSC பாடத்திட்டம் கையாள முடியாதது போல உணர்ந்திருந்தால், கட்-ஆஃப் பயம் இருந்திருந்தால், உங்களால் முடியுமா என்று நிச்சயமாக இல்லாமல் இருந்திருந்தால் — இந்த செயலி உங்களுக்காக. உங்களால் முடியும். உதவுவோம்.",
      valuesTitle: "எங்கள் கொள்கைகள்",
      values: [
        {
          title: "தமிழ் முதன்மை",
          desc: "ஒவ்வொரு அம்சமும் முதலில் தமிழில், பின்னர் ஆங்கிலத்தில் வடிவமைக்கப்படுகிறது. மாற்றம் இல்லை.",
        },
        {
          title: "நேர்மையான விலை",
          desc: "மாதம் ₹250. எப்போது வேண்டுமானாலும் ரத்து. தந்திரம் இல்லை, எதிர்பாராத ஆட்டோ-புதுப்பிப்பு இல்லை.",
        },
        {
          title: "மாணவரே மையம்",
          desc: "ஒவ்வொரு செய்தியையும் படிக்கிறோம். ஒவ்வொரு அம்சமும் ஒரு உண்மையான மாணவரின் கோரிக்கையிலிருந்து வருகிறது.",
        },
      ],
    },
    legal: {
      lastUpdated: "கடைசியாக புதுப்பிக்கப்பட்டது: ஜனவரி 2026",
    },
  },
};
